import { useEffect } from 'react';
import { ActionBar, ConfirmDialog, FormDialog, SearchDialog, Sidebar } from './components';
import { ErpDataTableV2 } from '@/components/custom/erp';
import { factorGroupColumns } from './cols-definition';
import { toast, useGroup, useForms } from '@/hooks';
import { Group, GroupType } from '@/types/schemas';
import { useData, useSearchState } from './hooks';
import { FormValues } from './schema';

export const groupTypes = [
  {
    label: 'Nhóm phi (PH1)',
    value: GroupType.FEE1,
    shortCode: 'PH1'
  },
  {
    label: 'Nhóm phi (PH2)',
    value: GroupType.FEE2,
    shortCode: 'PH2'
  },
  {
    label: 'Nhóm phi (PH3)',
    value: GroupType.FEE3,
    shortCode: 'PH3'
  }
];
const DEFAULT_GROUP = groupTypes[0].value;

export default function DanhMucNhomPhiPage() {
  const {
    groups: groupsFEE1,
    isLoading: isLoadingFEE1,
    refreshGroups: refreshGroupsFEE1,
    fetchGroupsByType: fetchGroupsByTypeFEE1,
    addGroup: addGroupFEE1,
    updateGroup: updateGroupFEE1,
    deleteGroup: deleteGroupFEE1
  } = useGroup(GroupType.FEE1);

  const {
    groups: groupsFEE2,
    isLoading: isLoadingFEE2,
    refreshGroups: refreshGroupsFEE2,
    fetchGroupsByType: fetchGroupsByTypeFEE2,
    addGroup: addGroupFEE2,
    updateGroup: updateGroupFEE2,
    deleteGroup: deleteGroupFEE2
  } = useGroup(GroupType.FEE2);

  const {
    groups: groupsFEE3,
    isLoading: isLoadingFEE3,
    refreshGroups: refreshGroupsFEE3,
    fetchGroupsByType: fetchGroupsByTypeFEE3,
    addGroup: addGroupFEE3,
    updateGroup: updateGroupFEE3,
    deleteGroup: deleteGroupFEE3
  } = useGroup(GroupType.FEE3);

  const getGroupFunctions = (groupType: GroupType) => {
    switch (groupType) {
      case GroupType.FEE1:
        return {
          groups: groupsFEE1,
          refreshGroups: refreshGroupsFEE1,
          fetchGroupsByType: fetchGroupsByTypeFEE1,
          addGroup: addGroupFEE1,
          updateGroup: updateGroupFEE1,
          deleteGroup: deleteGroupFEE1,
          isLoading: isLoadingFEE1
        };
      case GroupType.FEE2:
        return {
          groups: groupsFEE2,
          refreshGroups: refreshGroupsFEE2,
          fetchGroupsByType: fetchGroupsByTypeFEE2,
          addGroup: addGroupFEE2,
          updateGroup: updateGroupFEE2,
          deleteGroup: deleteGroupFEE2,
          isLoading: isLoadingFEE2
        };
      case GroupType.FEE3:
        return {
          groups: groupsFEE3,
          refreshGroups: refreshGroupsFEE3,
          fetchGroupsByType: fetchGroupsByTypeFEE3,
          addGroup: addGroupFEE3,
          updateGroup: updateGroupFEE3,
          deleteGroup: deleteGroupFEE3,
          isLoading: isLoadingFEE3
        };
      default:
        return {
          groups: groupsFEE1,
          refreshGroups: refreshGroupsFEE1,
          fetchGroupsByType: fetchGroupsByTypeFEE1,
          addGroup: addGroupFEE1,
          updateGroup: updateGroupFEE1,
          deleteGroup: deleteGroupFEE1,
          isLoading: isLoadingFEE1
        };
    }
  };

  const { activeGroup, handleFilter, sidebarOpen, toggleSidebar } = useData(DEFAULT_GROUP);
  const { isLoading, addGroup, updateGroup, deleteGroup, groups, refreshGroups } = getGroupFunctions(activeGroup);
  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<Group>();

  const { showSearchDialog, openSearchDialog, closeSearchDialog } = useSearchState(clearSelection);

  useEffect(() => {
    const currentGroupFunctions = getGroupFunctions(activeGroup);
    currentGroupFunctions.refreshGroups();
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup]);

  const handleSubmit = async (data: FormValues) => {
    try {
      const formData = {
        ...data,
        loai_nhom: activeGroup
      };

      if (formMode === 'add') {
        await addGroup(formData);
      } else if (formMode === 'edit' && selectedObj) {
        await updateGroup(selectedObj.uuid, formData);
      }

      handleCloseForm();
      clearSelection();
      await refreshGroups();
    } catch {
      let errorMessage = 'Có lỗi xảy ra, vui lòng thử lại sau.';

      toast({
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const tables = [
    {
      name: '',
      rows: groups,
      columns: factorGroupColumns,
      key: `${activeGroup}-${Date.now()}`
    }
  ];

  return (
    <div className='flex h-full min-h-screen w-full min-w-0 max-w-full flex-col lg:overflow-hidden'>
      {showForm && (
        <FormDialog
          open={showForm}
          onSubmit={handleSubmit}
          formMode={formMode}
          initialData={
            selectedObj && (formMode === 'edit' || formMode === 'view' || (formMode === 'add' && isCopyMode))
              ? selectedObj
              : formMode === 'add' && !isCopyMode
                ? undefined
                : undefined
          }
          onClose={handleCloseForm}
          onAdd={() => {
            handleCloseForm();
            clearSelection();
            setTimeout(() => {
              handleAddClick();
            }, 100);
          }}
          onEdit={() => {
            if (selectedObj) {
              handleCloseForm();
              handleEditClick();
            }
          }}
          onDelete={() => {
            if (selectedObj) {
              handleCloseForm();
              handleDeleteClick();
            }
          }}
          onCopy={() => {
            if (selectedObj) {
              handleCloseForm();
              handleCopyClick();
            }
          }}
        />
      )}

      {showDelete && (
        <ConfirmDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          onDelete={deleteGroup}
          clearSelection={clearSelection}
          title='Xoá dữ liệu'
          content='Bạn có chắc chắn muốn xoá không?'
        />
      )}

      {showSearchDialog && (
        <SearchDialog
          openSearchDialog={showSearchDialog}
          onCloseSearchDialog={closeSearchDialog}
          onSearch={async data => console.log('Search', data)}
        />
      )}

      <div className='flex h-full'>
        <Sidebar
          activeGroup={activeGroup}
          onFilterChange={handleFilter}
          isOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
        />

        <div className='flex h-full min-w-0 flex-1 flex-col overflow-hidden'>
          <ActionBar
            activeGroup={activeGroup}
            onAddClick={handleAddClick}
            onRefreshClick={refreshGroups}
            onExportClick={() => {}}
            onEditClick={() => selectedObj && handleEditClick()}
            onViewClick={() => selectedObj && handleViewClick()}
            onCopyClick={() => selectedObj && handleCopyClick()}
            onDeleteClick={() => selectedObj && handleDeleteClick()}
            onSearchClick={openSearchDialog}
            isViewDisabled={!selectedObj}
          />

          <div className='min-w-0 flex-1 overflow-hidden'>
            <ErpDataTableV2
              tables={tables}
              selectedRowId={selectedRowIndex || undefined}
              onRowClick={handleRowClick}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
